/**
 * ServerMonitoring.jsx
 * Dashboard de monitoring des performances serveur
 * Optimisé pour minimiser l'impact CPU avec polling intelligent
 */

import React, { useState, useEffect, useRef } from 'react';
import { Card, Row, Col, Progress, Statistic, Switch, Badge, Alert, Timeline, Spin } from 'antd';
import {
  DashboardOutlined,
  ThunderboltOutlined,
  DatabaseOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './ServerMonitoring.css';
import AccountRecoveryDeliveryMonitor from '../components/AccountRecoveryDeliveryMonitor';

// Enregistrer les composants Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const ServerMonitoring = () => {
  // Auth context
  const { token, user } = useAuth();

  // États
  const [metrics, setMetrics] = useState(null);
  const [history, setHistory] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [isPolling, setIsPolling] = useState(false); // OFF par défaut
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Références pour les intervalles
  const pollingInterval = useRef(null);
  const visibilityListenerAdded = useRef(false);

  // Configuration du polling
  // ⚡ FIX CPU SATURATION (3 Jan 2026): Réduit de 10s à 60s pour économiser CPU (-83% requêtes)
  const POLLING_INTERVAL = 60000; // 60 secondes (avant: 10000 = 10s)
  const MAX_HISTORY_POINTS = 30; // 30 minutes d'historique (30 points × 60s)

  // Configuration d'axios avec le token d'authentification
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
  }, [token]);

  /**
   * Récupère les métriques depuis l'API
   */
  const fetchMetrics = async () => {
    try {
      const response = await axios.get('/api/monitoring/metrics');
      const data = response.data;

      if (data.success) {
        setMetrics(data.data);
        setError(null);

        // Ajouter à l'historique
        setHistory(prev => {
          const newHistory = [...prev, {
            timestamp: new Date(data.data.timestamp),
            cpu: data.data.cpu.usage,
            memory: data.data.memory.percentage
          }];

          // Garder seulement les derniers MAX_HISTORY_POINTS
          if (newHistory.length > MAX_HISTORY_POINTS) {
            return newHistory.slice(-MAX_HISTORY_POINTS);
          }
          return newHistory;
        });
      }
    } catch (err) {
      console.error('[MONITORING] Erreur fetch metrics:', err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Récupère les recommandations
   */
  const fetchRecommendations = async () => {
    try {
      const response = await axios.get('/api/monitoring/recommendations');
      const data = response.data;

      if (data.success) {
        setRecommendations(data.data.recommendations);
      }
    } catch (err) {
      console.error('[MONITORING] Erreur fetch recommendations:', err);
    }
  };

  /**
   * Démarre le polling
   */
  const startPolling = () => {
    if (pollingInterval.current) return;

    console.log('[MONITORING] Démarrage du polling');
    fetchMetrics();
    fetchRecommendations();

    pollingInterval.current = setInterval(() => {
      fetchMetrics();
      // Recommandations moins fréquentes (toutes les 30s)
      if (Date.now() % 30000 < POLLING_INTERVAL) {
        fetchRecommendations();
      }
    }, POLLING_INTERVAL);
  };

  /**
   * Arrête le polling
   */
  const stopPolling = () => {
    if (pollingInterval.current) {
      console.log('[MONITORING] Arrêt du polling');
      clearInterval(pollingInterval.current);
      pollingInterval.current = null;
    }
  };

  /**
   * Gestion du switch ON/OFF
   */
  const handlePollingToggle = (checked) => {
    setIsPolling(checked);
    if (checked) {
      startPolling();
    } else {
      stopPolling();
    }
  };

  /**
   * Gestion de la visibilité de l'onglet
   */
  useEffect(() => {
    if (visibilityListenerAdded.current) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Onglet caché → arrêter le polling
        if (isPolling) {
          console.log('[MONITORING] Onglet caché, pause polling');
          stopPolling();
        }
      } else {
        // Onglet visible → reprendre le polling si activé
        if (isPolling) {
          console.log('[MONITORING] Onglet visible, reprise polling');
          startPolling();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    visibilityListenerAdded.current = true;

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      visibilityListenerAdded.current = false;
    };
  }, [isPolling]);

  /**
   * Nettoyage au démontage
   */
  useEffect(() => {
    // Premier chargement
    fetchMetrics();
    fetchRecommendations();

    return () => {
      stopPolling();
    };
  }, []);

  /**
   * Détermine la couleur selon le niveau
   */
  const getStatusColor = (percentage, type = 'cpu') => {
    if (type === 'cpu') {
      if (percentage > 70) return 'error';
      if (percentage > 40) return 'warning';
      return 'success';
    } else if (type === 'memory') {
      if (percentage > 80) return 'error';
      if (percentage > 50) return 'warning';
      return 'success';
    }
    return 'normal';
  };

  /**
   * Prépare les données pour le graphique
   */
  const getChartData = () => {
    if (history.length === 0) return null;

    return {
      labels: history.map(h => {
        const time = new Date(h.timestamp);
        return `${time.getHours().toString().padStart(2, '0')}:${time.getMinutes().toString().padStart(2, '0')}:${time.getSeconds().toString().padStart(2, '0')}`;
      }),
      datasets: [
        {
          label: 'CPU (%)',
          data: history.map(h => h.cpu),
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.1)',
          fill: true,
          tension: 0.4,
        },
        {
          label: 'Mémoire (%)',
          data: history.map(h => h.memory),
          borderColor: 'rgb(54, 162, 235)',
          backgroundColor: 'rgba(54, 162, 235, 0.1)',
          fill: true,
          tension: 0.4,
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Performance Serveur (5 dernières minutes)'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: (value) => value + '%'
        }
      }
    }
  };

  /**
   * Rendu du badge de statut
   */
  const renderStatusBadge = (level) => {
    const badges = {
      success: <Badge status="success" text="Optimal" />,
      warning: <Badge status="warning" text="Attention" />,
      error: <Badge status="error" text="Critique" />,
      critical: <Badge status="error" text="Critique" />
    };
    return badges[level] || <Badge status="default" text="Inconnu" />;
  };

  if (loading && !metrics) {
    return (
      <div className="monitoring-loading">
        <Spin size="large" tip="Chargement des métriques..." />
      </div>
    );
  }

  return (
    <div className="server-monitoring-container">
      {/* Header avec contrôle ON/OFF */}
      <div className="monitoring-header">
        <div className="monitoring-title">
          <DashboardOutlined style={{ fontSize: '24px', marginRight: '12px' }} />
          <h1>Monitoring Serveur</h1>
        </div>

        <div className="monitoring-controls">
          <span style={{ marginRight: '12px', fontSize: '14px' }}>
            {isPolling ? (
              <>
                <PlayCircleOutlined style={{ color: '#52c41a', marginRight: '6px' }} />
                Actualisation automatique (10s)
              </>
            ) : (
              <>
                <PauseCircleOutlined style={{ color: '#faad14', marginRight: '6px' }} />
                Monitoring en pause
              </>
            )}
          </span>
          <Switch
            checked={isPolling}
            onChange={handlePollingToggle}
            checkedChildren="ON"
            unCheckedChildren="OFF"
          />
        </div>
      </div>

      {Number(user?.is_superadmin) === 1 && <AccountRecoveryDeliveryMonitor />}

      {error && (
        <Alert
          message="Erreur de connexion"
          description={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: '20px' }}
        />
      )}

      {metrics && (
        <>
          {/* Cartes de métriques principales */}
          <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
            {/* CPU */}
            <Col xs={24} sm={12} lg={8}>
              <Card className="metric-card">
                <div className="metric-header">
                  <ThunderboltOutlined className="metric-icon cpu" />
                  <span className="metric-label">CPU</span>
                  {renderStatusBadge(getStatusColor(metrics.cpu.usage, 'cpu'))}
                </div>
                <Statistic
                  value={metrics.cpu.usage}
                  precision={2}
                  suffix="%"
                  valueStyle={{
                    color: getStatusColor(metrics.cpu.usage, 'cpu') === 'error' ? '#cf1322' :
                           getStatusColor(metrics.cpu.usage, 'cpu') === 'warning' ? '#faad14' : '#3f8600'
                  }}
                />
                <Progress
                  percent={metrics.cpu.usage}
                  status={getStatusColor(metrics.cpu.usage, 'cpu')}
                  showInfo={false}
                  strokeWidth={12}
                />
                <div className="metric-details">
                  <span>Cœurs: {metrics.cpu.cores}</span>
                </div>
              </Card>
            </Col>

            {/* Mémoire */}
            <Col xs={24} sm={12} lg={8}>
              <Card className="metric-card">
                <div className="metric-header">
                  <DatabaseOutlined className="metric-icon memory" />
                  <span className="metric-label">Mémoire</span>
                  {renderStatusBadge(getStatusColor(metrics.memory.percentage, 'memory'))}
                </div>
                <Statistic
                  value={metrics.memory.percentage}
                  precision={2}
                  suffix="%"
                  valueStyle={{
                    color: getStatusColor(metrics.memory.percentage, 'memory') === 'error' ? '#cf1322' :
                           getStatusColor(metrics.memory.percentage, 'memory') === 'warning' ? '#faad14' : '#3f8600'
                  }}
                />
                <Progress
                  percent={metrics.memory.percentage}
                  status={getStatusColor(metrics.memory.percentage, 'memory')}
                  showInfo={false}
                  strokeWidth={12}
                />
                <div className="metric-details">
                  <span>{metrics.memory.used} GB / {metrics.memory.total} GB</span>
                </div>
              </Card>
            </Col>

            {/* Timers */}
            <Col xs={24} sm={12} lg={8}>
              <Card className="metric-card">
                <div className="metric-header">
                  <ClockCircleOutlined className="metric-icon timers" />
                  <span className="metric-label">Timers Actifs</span>
                  {metrics.timers.active > 50 ? (
                    <Badge status="warning" text="Élevé" />
                  ) : (
                    <Badge status="success" text="Normal" />
                  )}
                </div>
                <Statistic
                  value={metrics.timers.active}
                  valueStyle={{
                    color: metrics.timers.active > 50 ? '#faad14' : '#3f8600'
                  }}
                />
                <div className="metric-details">
                  <span>Handles: {metrics.timers.handles}</span>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Graphique */}
          {history.length > 0 && (
            <Card
              title="Tendance Performance"
              style={{ marginBottom: '20px' }}
              className="chart-card"
            >
              <div style={{ height: '300px' }}>
                <Line data={getChartData()} options={chartOptions} />
              </div>
            </Card>
          )}

          {/* Recommandations */}
          {recommendations.length > 0 && (
            <Card
              title={
                <span>
                  {recommendations[0].level === 'critical' ? <WarningOutlined style={{ color: '#cf1322', marginRight: '8px' }} /> :
                   recommendations[0].level === 'warning' ? <WarningOutlined style={{ color: '#faad14', marginRight: '8px' }} /> :
                   <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />}
                  Recommandations
                </span>
              }
              className="recommendations-card"
            >
              <Timeline>
                {recommendations.map((rec, index) => (
                  <Timeline.Item
                    key={index}
                    color={rec.level === 'critical' ? 'red' : rec.level === 'warning' ? 'orange' : 'green'}
                  >
                    <div className="recommendation-item">
                      <strong>{rec.message}</strong>
                      {rec.suggestions && rec.suggestions.length > 0 && (
                        <ul>
                          {rec.suggestions.map((suggestion, i) => (
                            <li key={i}>{suggestion}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </Timeline.Item>
                ))}
              </Timeline>
            </Card>
          )}

          {/* Informations système */}
          <Card title="Informations Système" className="system-info-card">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Statistic title="Uptime" value={metrics.process.uptime} suffix="min" />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic title="RSS" value={metrics.process.rss} suffix="MB" />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic title="Heap Utilisé" value={metrics.process.heapUsed} suffix="MB" />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic title="Node.js" value={metrics.system.nodeVersion} />
              </Col>
            </Row>
          </Card>
        </>
      )}
    </div>
  );
};

export default ServerMonitoring;
